/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { OrderItem, SuccesfullPartialPayment } from "@lemmonhq/types";
import {
  getRemainingQuantity,
  calculatePartialTotal,
} from "@app/utils/payments";
import { formatPrice } from "@lemmonhq/orders";
import { cn } from "@lemmonhq/uikit";
import { getItemPaymentStatus } from "./paymentUtils";
import { useAuth } from "@app/vue/providers/Auth";

interface PaymentItemProps {
  item: OrderItem;
  selectedQuantity?: number;
  onSelect?: (itemId: number, quantity: number) => void;
  partialPayments: SuccesfullPartialPayment[];
}

export const PaymentItem = defineComponent<PaymentItemProps>({
  name: "PaymentItem",
  props: ["item", "selectedQuantity", "onSelect", "partialPayments"],
  setup(props) {
    const auth = useAuth();
    const remainingQuantity = computed(() => {
      const remQty = getRemainingQuantity(props.item, props.partialPayments);
      //console.log({ remQty, item: props.item, pp: props.partialPayments });
      return remQty;
    });

    const itemTotal = computed(() =>
      calculatePartialTotal([props.item], {
        [props.item.id]: props.selectedQuantity || remainingQuantity.value,
      }),
    );

    return () => {
      const hasSelectedQuantity =
        props.selectedQuantity !== undefined &&
        Number.isFinite(props.selectedQuantity) &&
        props.selectedQuantity > 0;
      const itemStatus = getItemPaymentStatus(
        props.item,
        props.partialPayments,
      );
      if (itemStatus.status === "paid") {
        return (
          <div class="flex justify-between items-center p-3 border  rounded opacity-50">
            <div class="flex items-center gap-2 flex-1">
              <div class="w-8"></div>
              <div class="flex flex-1">
                <span class="text-accent cursor-pointer flex-1 pr-2">
                  {props.item.product.name}
                </span>
                <span class="  ml-2 flex">
                  <div class="font-bold text-success mr-2">PAID</div>{" "}
                  <span class="w-16 bg-neutral px-2 rounded text-center ">
                    {props.item.quantity}
                  </span>
                </span>
              </div>
            </div>
          </div>
        );
      } else if (itemStatus.status === "cancelled") {
        return (
          <div class="flex justify-between items-center p-3 border  rounded opacity-50">
            <div class="flex items-center gap-2 flex-1">
              <div class="w-8"></div>
              <div class="flex flex-1">
                <span
                  style={`text-decoration: line-through; text-decoration-thickness: 4px;`}
                  class="text-accent flex-1 pr-2"
                >
                  {props.item.product.name}
                </span>
                <span class="text-warning mr-2 font-bold ">CANCELLED</span>{" "}
                <span class="w-16 bg-neutral px-2 rounded text-center ">
                  {props.item.quantity}
                </span>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div class="flex justify-between items-center p-3 border  rounded">
          <div class="flex items-center gap-2 flex-1">
            <label class="cursor-pointer label p-0 flex justify-center items-center">
              {props.onSelect ? (
                <input
                  type="checkbox"
                  class="checkbox checkbox-sm"
                  checked={hasSelectedQuantity}
                  onChange={(e) => {
                    if (props.onSelect)
                      props.onSelect(
                        props.item.id,
                        (e.target as HTMLInputElement).checked
                          ? remainingQuantity.value
                          : 0,
                      );
                  }}
                />
              ) : null}
            </label>
            <div class="flex flex-1">
              <div
                style=""
                class={cn(
                  "bg-transparent border-none pr-2 flex-1 min-h-8 flex justify-start items-center cursor-pointer flex-1",
                  hasSelectedQuantity ? "text-accent" : "text-accent/80",
                )}
                onClick={() => {
                  if (props.onSelect)
                    props.onSelect(
                      props.item.id,
                      hasSelectedQuantity ? 0 : remainingQuantity.value,
                    );
                }}
              >
                {props.item.product.name}
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 text-accent">
            {hasSelectedQuantity ? null : (
              <span class="text-accent/50">
                {formatPrice(
                  itemTotal.value,
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </span>
            )}

            <button
              onClick={() =>
                props.onSelect &&
                props.onSelect(
                  props.item.id,
                  Math.max(0, (props.selectedQuantity || 0) - 1),
                )
              }
              class={
                "btn btn-xs btn-circle btn-neutral " +
                (hasSelectedQuantity ? "" : "hidden")
              }
            >
              <span class="material-symbols-outlined text-sm">remove</span>
            </button>
            <span class="w-16 bg-neutral px-2 rounded text-center font-mono">
              {(function () {
                if (hasSelectedQuantity) {
                  return `${
                    hasSelectedQuantity
                      ? props.selectedQuantity
                      : remainingQuantity.value
                  }/${props.item.quantity}`;
                } else {
                  return props.item.quantity;
                }
              })()}
            </span>
            <button
              onClick={() =>
                props.onSelect &&
                props.onSelect(
                  props.item.id,
                  Math.min(
                    remainingQuantity.value,
                    (props.selectedQuantity || 0) + 1,
                  ),
                )
              }
              class={
                "btn btn-xs btn-circle btn-neutral " +
                (hasSelectedQuantity ? "" : "hidden")
              }
              disabled={
                (props.selectedQuantity || 0) >= remainingQuantity.value
              }
            >
              <span class="material-symbols-outlined text-sm">add</span>
            </button>
          </div>
        </div>
      );
    };
  },
});
